// src/modules/pledgeReleaseCase/api.js
import API from '../../services/api';
import { headerSafeFileName } from '../../utils/headerSafeFileName';

// ── Pledge Release Cases ──

// Fetch all pledge release cases with search, pagination & optional status filter
export const fetchPledgeReleaseCases = ({ search = '', page = 1, limit = 10, status = '', inventoryStatus = '', branch = '', createdAfter = '', createdBefore = '', includeGoldReceipt = false, ...extra } = {}) =>
  API.get('/pledge-release-cases', {
    params: {
      search,
      page,
      limit,
      ...(status ? { status } : {}),
      ...(inventoryStatus ? { inventoryStatus } : {}),
      ...(branch ? { branch } : {}),
      ...(createdAfter ? { createdAfter } : {}),
      ...(createdBefore ? { createdBefore } : {}),
      ...(includeGoldReceipt ? { includeGoldReceipt: 'true' } : {}),
      // searchField, metalType, paymentMode, agentId, minAmount, maxAmount — blanks dropped
      ...Object.fromEntries(Object.entries(extra).filter(([, v]) => v !== '' && v !== null && v !== undefined)),
    },
  }).then((res) => res.data);

// Get a single case by ID
export const getPledgeReleaseCaseById = (id) =>
  API.get(`/pledge-release-cases/${id}`).then((res) => res.data);

// Create a new case
export const createPledgeReleaseCase = (data) =>
  API.post('/pledge-release-cases', data).then((res) => res.data);

// Update an existing case
export const updatePledgeReleaseCase = (id, data) =>
  API.put(`/pledge-release-cases/${id}`, data).then((res) => res.data);

// Update status only
export const updatePledgeReleaseCaseStatus = (id, status) =>
  API.patch(`/pledge-release-cases/${id}/status`, { status }).then((res) => res.data);

// Delete a case
// Soft delete by default; { permanent: true } is an admin-only hard delete.
export const deletePledgeReleaseCase = (id, { permanent = false } = {}) =>
  API.delete(`/pledge-release-cases/${id}`, { params: permanent ? { permanent: true } : {} }).then((res) => res.data);

export const restorePledgeReleaseCase = (id) =>
  API.post(`/pledge-release-cases/${id}/restore`).then((res) => res.data);

// ── Reconciliation (approval-gated correction of a locked case) ──

// Current reconciliation, its history, and which targets can still be reopened
export const getReconcileState = (caseId) =>
  API.get(`/pledge-release-cases/${caseId}/reconcile`).then((res) => res.data);

// Raise a reconciliation request. target: 'GOLD_RECEIPT' | 'CASE_DETAILS'
export const requestReconcile = (caseId, { target, reason }) =>
  API.post(`/pledge-release-cases/${caseId}/reconcile`, { target, reason }).then((res) => res.data);

// Withdraw a request that has not been used yet
export const cancelReconcile = (caseId) =>
  API.delete(`/pledge-release-cases/${caseId}/reconcile`).then((res) => res.data);

// ── Document Upload ──

// Upload a raw file buffer to ImageKit
export const uploadDocument = async (file, folderPath) => {
  const fileName = file.name || `doc_${Date.now()}.pdf`;
  const headers = {
    'Content-Type': 'application/octet-stream',
    'x-file-name': headerSafeFileName(fileName),
  };
  if (folderPath) headers['x-folder-path'] = folderPath;
  const response = await API.post('/uploads/buffer', file, {
    headers,
    transformRequest: [(data) => data],
    maxBodyLength: Infinity,
  });
  return response.data;
};

// Add document URL to case
export const addDocumentToCase = (caseId, docData) =>
  API.post(`/pledge-release-cases/${caseId}/documents`, docData).then((res) => res.data);

// Remove document from case by index
export const removeDocumentFromCase = (caseId, docIndex) =>
  API.delete(`/pledge-release-cases/${caseId}/documents/${docIndex}`).then((res) => res.data);

// Submit all verification documents as one batch — triggers PLEDGE_DOC_SUBMISSION workflow
export const submitVerificationDocuments = (caseId, payload) =>
  API.post(`/pledge-release-cases/${caseId}/submit-documents`, payload).then((res) => res.data);

// Cancel a pending document submission and reset verification status to NOT_SUBMITTED
export const revertDocumentSubmission = (caseId) =>
  API.post(`/pledge-release-cases/${caseId}/revert-documents`).then((res) => res.data);

export const generateVerificationForm = (caseId, payload) =>
  API.post(`/pledge-release-cases/${caseId}/generate-verification-form`, payload, { responseType: 'blob' }).then(res => res.data);


export const fetchDocApprovalInstance = (caseId) =>
  API.get(`/pledge-release-cases/${caseId}/doc-approval-instance`).then((res) => res.data);

// ── Advance Payment Ledger ──

// Create a new advance payment
export const createAdvancePayment = (data) =>
  API.post('/advance-payments', data).then((res) => res.data);

// Get payments for a specific case
export const fetchAdvancePayments = (caseId, { page = 1, limit = 50 } = {}) =>
  API.get('/advance-payments', { params: { caseId, page, limit } }).then((res) => res.data);

// Delete a specific advance payment (also removes linked vouchers)
export const deleteAdvancePayment = (paymentId) =>
  API.delete(`/advance-payments/${paymentId}`).then((res) => res.data);

// Get exposure summary
export const getExposureSummary = () =>
  API.get('/advance-payments/exposure-summary').then((res) => res.data);

// ── Gold Receipts ──

// Create a gold receipt for a case
export const createGoldReceipt = (data) =>
  API.post('/gold-receipts', data).then((res) => res.data);

// Correct an existing gold receipt. Only accepted while the case carries an
// approved reconciliation for the GOLD_RECEIPT target — and it spends it.
export const updateGoldReceipt = (caseId, data) =>
  API.put(`/gold-receipts/${caseId}`, data).then((res) => res.data);

// Get gold receipt for a specific case
export const getGoldReceiptByCase = (caseId) =>
  API.get(`/gold-receipts/${caseId}`).then((res) => res.data);

// List all gold receipts
export const fetchGoldReceipts = ({ page = 1, limit = 20 } = {}) =>
  API.get('/gold-receipts', { params: { page, limit } }).then((res) => res.data);

// Compute & persist final settlement for a case
export const settleCase = (caseId) =>
  API.post(`/gold-receipts/${caseId}/settle`).then((res) => res.data);

// Lock in what the customer is owed (and profit / agent commission) so the
// settlement payouts can begin. Runs BEFORE the bill — the bill is the last step.
export const finalizeSettlement = (caseId) =>
  API.post(`/pledge-release-cases/${caseId}/finalize-settlement`).then((res) => res.data);

// Generate the final bill against a completed settlement & close the case
export const generateFinalBill = (caseId, { paymentMode, remarks } = {}) =>
  API.post(`/pledge-release-cases/${caseId}/generate-final-bill`, { paymentMode, remarks }).then((res) => res.data);

// ── Voucher APIs ──

// Preview voucher PDF without saving (Step 1 of 2-step workflow)
export const previewVoucher = (caseId, paymentDetails) =>
  API.post(`/advance-payments/${caseId}/preview-voucher`, { paymentDetails }).then((res) => res.data);

// Confirm and save voucher after user approval (Step 2 of 2-step workflow)
export const confirmVoucher = (caseId, paymentDetails, regenerate = false) =>
  API.post(`/advance-payments/${caseId}/confirm-voucher`, { paymentDetails, regenerate }).then((res) => res.data);

// Get vouchers by case ID
export const getVouchersByCase = (caseId, { page = 1, limit = 10 } = {}) =>
  API.get('/advance-vouchers', { params: { caseId, page, limit } }).then((res) => res.data);

// ── Gold Agreement APIs ──

// Preview agreement PDF (returns base64, no DB save)
export const previewAgreement = (caseId, agreementType) =>
  API.post(`/gold-agreements/${caseId}/preview`, { agreementType }).then((res) => res.data);

// Confirm agreement (generate + upload + save)
export const confirmAgreement = (caseId, agreementType) =>
  API.post(`/gold-agreements/${caseId}/confirm`, { agreementType }).then((res) => res.data);

// Regenerate agreement from existing snapshot
export const regenerateAgreement = (agreementId) =>
  API.post(`/gold-agreements/${agreementId}/regenerate`).then((res) => res.data);

// List agreements (optional caseId filter)
export const listAgreements = (caseId, { page = 1, limit = 20 } = {}) =>
  API.get('/gold-agreements', { params: { caseId, page, limit } }).then((res) => res.data);

// Get single agreement by agreementId
export const getAgreementById = (agreementId) =>
  API.get(`/gold-agreements/${agreementId}`).then((res) => res.data);

// Start eSign on a confirmed agreement
export const startAgreementESign = (agreementId) =>
  API.post(`/gold-agreements/${agreementId}/esign/start`).then((res) => res.data);

// Finalize eSign after customer signs
export const finalizeAgreementESign = (agreementId) =>
  API.post(`/gold-agreements/${agreementId}/esign/finalize`).then((res) => res.data);

// Get specific voucher by voucher ID
export const getVoucher = (voucherId) =>
  API.get(`/advance-vouchers/${voucherId}`).then((res) => res.data);

// Regenerate voucher PDF
export const regenerateVoucher = (voucherId, reason = '') =>
  API.post(`/advance-vouchers/${voucherId}/regenerate`, { reason }).then((res) => res.data);

// Create voucher from settlement payment (supports both Pledge and WalkIn cases)
export const createSettlementPaymentVoucher = (paymentId, fundId = null) =>
  API.post(`/advance-vouchers/settlement-voucher/${paymentId}`, { fundId }).then((res) => res.data);

// Delete a specific voucher
export const deleteVoucher = (voucherId) =>
  API.delete(`/advance-vouchers/${voucherId}`).then((res) => res.data);

// ── Settlement Payments ──

// Create a new settlement payment (pay customer from net settlement amount)
export const createSettlementPayment = (data) =>
  API.post('/settlement-payments', data).then((res) => res.data);

// Get settlement payments for a specific case
export const fetchSettlementPayments = (caseId, { page = 1, limit = 50 } = {}) =>
  API.get('/settlement-payments', { params: { caseId, page, limit } }).then((res) => res.data);

// Delete a specific settlement payment
export const deleteSettlementPayment = (paymentId) =>
  API.delete(`/settlement-payments/${paymentId}`).then((res) => res.data);

// ── Treasury Settings (public read) ──

// Returns { data: { cashApprovalThreshold: number } }
// Pass the case's branchId to get that branch's override (falls back to global).
export const fetchCashThreshold = (branchId) =>
  API.get('/treasury/settings/cash-threshold', { params: branchId ? { branchId } : {} }).then((res) => res.data);

// ── Settlement Payment Vouchers (SEPARATE from Advance Payment Vouchers) ──

// Get settlement payment vouchers for a case
export const getSettlementPaymentVouchers = (caseId, { page = 1, limit = 50 } = {}) =>
  API.get('/settlement-payment-vouchers', { params: { caseId, page, limit } }).then((res) => res.data);

// Delete a settlement payment voucher
export const deleteSettlementPaymentVoucher = (voucherId) =>
  API.delete(`/settlement-payment-vouchers/${voucherId}`).then((res) => res.data);

// Regenerate a settlement payment voucher PDF
export const regenerateSettlementPaymentVoucher = (voucherId, reason = '') =>
  API.post(`/settlement-payment-vouchers/${voucherId}/regenerate`, { reason }).then((res) => res.data);

// Get a specific settlement payment voucher
export const getSettlementPaymentVoucher = (voucherId) =>
  API.get(`/settlement-payment-vouchers/${voucherId}`).then((res) => res.data);
